import { useAppSelector } from '@brands/hooks/useReduxHook';
import { selectAuth } from '@brands/store/selectors/auth';
import { yupResolver } from '@hookform/resolvers/yup';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Alert, AlertTitle, ToggleButton, ToggleButtonGroup } from '@mui/material';
import cn from 'classnames';
import { E164Number, isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import Button from '../../../../../Components/Button/Button';
import PhoneInput from '@brands/Components/Inputs/MuiTelInput/MuiTelInput';
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import ProfileImage from '../../../../../Components/ProfileImage/ProfileImage';
import { RegisterMediaPayload } from '../../../../../services/communication/registersConversationMedia';
import { confirmAvatarMedia } from '../../../../../services/identity/confirmAvatarMedia';
import { createBasicInformation } from '../../../../../services/identity/createBasicInformation';
import { createNewUser, fullNameFromFirstAndLast } from '../../../../../services/identity/createNewUser';
import { deleteAvatar } from '../../../../../services/identity/deleteAvatar';
import { listAllClientTags } from '../../../../../services/identity/listAllClientTags';
import { updateUserClientTag } from '../../../../../services/identity/updateUserClientTag';
import { scopeClientTagOptionsToActor } from '../../../../../services/identity/clientTagActorScope';
import { registerAvatarMedia } from '../../../../../services/identity/registerAvatarMedia';
import { BasicInfo, ClientTag, UserProfile, UserRoleName } from '../../../../../services/identity/types/UserProfile';
import {
  getAllowedDivisionIds,
  getClientDivisionOptions,
} from '../../../../../services/identity/clientDivisionOptions';
import { displayErrorDetails, withoutErrorCode } from '../../../../../Utils/displayError';
import styles from '../styles.module.scss';
import { collectorCreateUserValidationSchema } from './validationSchema';
import MultiSelectInputMUI from '@brands/Components/Inputs/MuiMultiSelectInput/MultiSelectInputMUI';
import { ClientTagOption } from '../../../../MyProfile/CareAdvisor/utils/types';

export type AddCollectorFormValues = {
  profile_img: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  client_tags?: ClientTagOption[];
  client_divisions?: ClientTagOption[];
};

interface ProfileCardProps {
  onClose: () => void;
}

function getDefaultCollectorValues(): AddCollectorFormValues {
  return {
    profile_img: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    client_tags: [],
    client_divisions: [],
  };
}

const AddCollectorUserPage: FC<ProfileCardProps> = ({ onClose }) => {
  const { userInfo } = useAppSelector(selectAuth);

  const [currentAvatarURL, setCurrentAvatarURL] = useState<string>('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPayload, setAvatarPayload] = useState<RegisterMediaPayload | null>(null);
  const [clientTagOptions, setClientTagOptions] = useState<ClientTagOption[]>([]);
  const [allClientTags, setAllClientTags] = useState<ClientTag[]>([]);
  const [avatarAction, setAvatarAction] = useState<string>('');
  const [image, setImage] = React.useState<string>();
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSendingInvitation, setIsSendingInvitation] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    getValues,
    watch,
  } = useForm<AddCollectorFormValues>({
    mode: 'onChange',
    resolver: yupResolver(collectorCreateUserValidationSchema()),
    defaultValues: getDefaultCollectorValues(),
  });

  const watchedClientTags = watch('client_tags') || [];
  const divisionOptions = useMemo(
    () =>
      getClientDivisionOptions(
        allClientTags,
        watchedClientTags.map((tag) => tag.value),
      ).map((option) => ({
        id: option.id,
        value: String(option.value),
        label: option.label,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allClientTags, JSON.stringify(watchedClientTags)],
  );

  const triggerProfileAvatar = useCallback(
    (avatarUrl: string) => {
      setValue('profile_img', avatarUrl, { shouldValidate: true });
    },
    [setValue],
  );
  // No organization is picked anymore (every user belongs to Dentistry One), so there is no
  // organization domain to invite from; use the site the admin is on.
  const getInvitationDomain = (): string => {
    const currentDomain = window.location.hostname;
    return currentDomain === 'localhost' || currentDomain.startsWith('localhost')
      ? 'develop.dentistry.one'
      : currentDomain;
  };

  const loadClientTags = useCallback(async () => {
    try {
      const { nodes: tags } = await listAllClientTags();
      const mapped = (tags || []).map((c) => ({ id: String(c.id), value: c.id, label: c.name }));
      setClientTagOptions(scopeClientTagOptionsToActor(mapped, userInfo));
      setAllClientTags(tags || []);
    } catch (e) {
      displayErrorDetails(e);
    }
  }, []);

  useEffect(() => {
    loadClientTags();
  }, [loadClientTags]);
  const setTextValue = (e: React.ChangeEvent<HTMLInputElement>, name: string): void => {
    const { value } = e.target;
    setValue(name as keyof AddCollectorFormValues, value, { shouldValidate: true });
  };

  const onInvalid = (errorsList: Record<string, unknown>): void => {
    const error = Object.values(errorsList)[0] as { message?: string } | undefined;
    if (error?.message) {
      toast.error(error.message, { className: 'fs-unmask' });
    }
  };

  const onCancelEditProfile = (): void => {
    onClose();
    setImage(currentAvatarURL);
  };

  const callSaveProfile = async (data: AddCollectorFormValues): Promise<void> => {
    let newUser: UserProfile | undefined;
    setIsSavingProfile(true);

    const phoneRaw = data.phone_number;
    const phoneFormatted =
      phoneRaw && isValidPhoneNumber(String(phoneRaw), 'US')
        ? (parsePhoneNumber(String(phoneRaw), 'US')?.format('E.164') as string)
        : undefined;

    const client_ids = (data?.client_tags || [])
      .map((tag) => Number(tag.id))
      .filter((n) => Number.isInteger(n) && n > 0);
    const fullNameForCreateUser = fullNameFromFirstAndLast(data.first_name, data.last_name);

    try {
      newUser = await createNewUser({
        email: data.email?.toLowerCase(),
        ...(phoneFormatted ? { phone_number: phoneFormatted } : {}),
        type: UserRoleName.Collector,
        ...(client_ids.length > 0 && { client_ids }),
        send_invitation: isSendingInvitation,
        // Every user belongs to Dentistry One; the backend adds its default division/practice.
        organization_ids: ['dentistry-one'],
        frontend_website: `https://${getInvitationDomain()}`,
      });
    } catch (error: unknown) {
      displayErrorDetails(error);
      setIsSavingProfile(false);
      return;
    }

    if (newUser) {
      try {
        const client_division_ids = (data?.client_divisions || [])
          .map((division) => Number(division.id))
          .filter((n) => Number.isInteger(n) && n > 0);
        if (client_division_ids.length > 0) {
          await updateUserClientTag(newUser.id, { client_ids, client_division_ids });
        }
        if (avatarPayload && avatarFile && avatarAction === 'Upload') {
          const res = await registerAvatarMedia(avatarPayload);
          const mediaId = res.files[0].upload_id;
          await fetch(res.files[0].upload_url, {
            method: 'PUT',
            body: avatarFile,
            headers: { 'content-type': avatarFile.type },
          });
          await confirmAvatarMedia(mediaId, newUser.id);
          triggerProfileAvatar(res.files[0].upload_url);
        } else if (avatarAction === 'Remove') {
          await deleteAvatar(newUser.id);
          triggerProfileAvatar('');
        }

        if (data.first_name || data.middle_name || data.last_name) {
          await createBasicInformation(
            {
              ...(data.first_name && { first_name: data.first_name }),
              ...(data.middle_name && { middle_name: data.middle_name }),
              ...(data.last_name && { last_name: data.last_name }),
            } as BasicInfo,
            newUser.id,
          );
        }

        toast.success('Collector user created.', { className: 'fs-unmask' });
        onClose();
      } catch (error: any) {
        if (error.response) {
          if (error.response.data.message && error.response.data.details) {
            toast.error(withoutErrorCode(`${error.response.data.message} \n${error.response.data.details}`), {
              className: 'fs-unmask',
            });
          } else if (error.response.data.details) {
            toast.error(withoutErrorCode(error.response.data.details), { className: 'fs-unmask' });
          } else {
            toast.error(withoutErrorCode(error.response.data.message), { className: 'fs-unmask' });
          }
        } else {
          toast.error(error.message, { className: 'fs-unmask' });
        }
      }
    }
    setIsSavingProfile(false);
  };

  const formContent = (
    <>
      <div className={cn(styles.profileContainer, styles.newDesignProfileContainer, styles.providerPixelPerfect)}>
        <div className={styles.contentContainer}>
          <div className={styles.contentContainerInner}>
            <div className={styles.editProfileContainer}>
              <div className={styles.backButtonContainer}>
                <button type="button" className={styles.backButton} onClick={onClose}>
                  <ChevronLeftIcon className={styles.backButtonIcon} aria-hidden />
                  Back
                </button>
              </div>
              <Alert severity="info" className={styles.createUserNotice} style={{ marginBottom: '24px' }}>
                <AlertTitle>Creating collector profile</AlertTitle>
              </Alert>
              <div className={styles.providerInviteSection}>
                <div className={styles.providerInviteTitle}>Send invitation to user?</div>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={isSendingInvitation ? 'yes' : 'no'}
                  onChange={(_, value) => {
                    if (value !== null) {
                      setIsSendingInvitation(value === 'yes');
                    }
                  }}
                  className={styles.providerInviteToggle}
                >
                  <ToggleButton value="yes">Yes</ToggleButton>
                  <ToggleButton value="no">No</ToggleButton>
                </ToggleButtonGroup>
              </div>
              <div className={styles.separator} />
              <div className={styles.titleContainer}>
                <span className={styles.title}>Basic information</span>
              </div>
              <div className={styles.editContainer}>
                <form className={styles.formContainer} onSubmit={handleSubmit(callSaveProfile, onInvalid)}>
                  <div className={styles.headerActionDiv}>
                    <ProfileImage
                      reviewProfile
                      setCurrentAvatarURL={setCurrentAvatarURL}
                      setAvatarFile={setAvatarFile}
                      setAvatarPayload={setAvatarPayload}
                      setAvatarAction={setAvatarAction}
                      triggerProfileAvatar={triggerProfileAvatar}
                      setImage={setImage}
                      image={image}
                      errors={errors}
                      size="80px"
                      isNewDependent
                    />
                  </div>

                  <div className={styles.row}>
                    <div className={cn(styles.rowItem, styles.detailsCA)}>
                      <TextField
                        {...register('first_name')}
                        label="First Name"
                        required
                        errors={errors}
                        name="first_name"
                        value={getValues('first_name')}
                        onChange={(e) => setTextValue(e, 'first_name')}
                        wrapperClassName={styles.inputWrapper}
                        inputClass={styles.input}
                      />
                    </div>
                    <div className={cn(styles.rowItem, styles.detailsCA)}>
                      <TextField
                        {...register('middle_name')}
                        label="Middle Name"
                        errors={errors}
                        name="middle_name"
                        value={getValues('middle_name')}
                        onChange={(e) => setTextValue(e, 'middle_name')}
                        wrapperClassName={styles.inputWrapper}
                        inputClass={styles.input}
                      />
                    </div>
                    <div className={cn(styles.rowItem, styles.detailsCA)}>
                      <TextField
                        {...register('last_name')}
                        label="Last Name"
                        required
                        errors={errors}
                        name="last_name"
                        value={getValues('last_name')}
                        onChange={(e) => setTextValue(e, 'last_name')}
                        wrapperClassName={styles.inputWrapper}
                        inputClass={styles.input}
                      />
                    </div>
                  </div>

                  <div className={styles.row}>
                    <div className={cn(styles.rowItem, styles.rowItemHalf)}>
                      <TextField
                        {...register('email')}
                        label="Email Address"
                        required
                        errors={errors}
                        name="email"
                        value={getValues('email')}
                        onChange={(e) => setTextValue(e, 'email')}
                        wrapperClassName={styles.inputWrapper}
                        inputClass={styles.input}
                      />
                    </div>
                    <div className={cn(styles.rowItem, styles.rowItemHalf)}>
                      <div className={styles.inputWrapper}>
                        <PhoneInput
                          {...register('phone_number')}
                          label="Phone Number"
                          required
                          id="phone_number_collector"
                          value={getValues('phone_number') as E164Number}
                          onChange={(val) => {
                            setValue('phone_number', val as string, {
                              shouldValidate: true,
                            });
                          }}
                          inputClass={styles.input}
                          errors={errors}
                          wrapperClassName={styles.inputWrapper}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={styles.row}>
                    <div className={styles.rowItem}>
                      <MultiSelectInputMUI
                        {...register('client_tags')}
                        name="client_tags"
                        label="Client tags"
                        withId
                        required
                        options={clientTagOptions}
                        wrapperClassName={styles.inputWrapper}
                        errors={errors}
                        value={(getValues('client_tags') || []).map((tag) => tag.id)}
                        onChange={(ids: string[]) => {
                          const selectedOptions = clientTagOptions.filter((option) => ids.includes(option.id));
                          setValue('client_tags', selectedOptions, { shouldValidate: true });
                          const allowedDivisionIds = getAllowedDivisionIds(
                            allClientTags,
                            selectedOptions.map((option) => option.value),
                          );
                          setValue(
                            'client_divisions',
                            (getValues('client_divisions') || []).filter((division) =>
                              allowedDivisionIds.has(String(division.id)),
                            ),
                            { shouldValidate: true },
                          );
                        }}
                      />
                    </div>
                  </div>
                  {(watch('client_tags') || []).length > 0 && divisionOptions.length > 0 && (
                    <div className={styles.row}>
                      <div className={styles.rowItem}>
                        <MultiSelectInputMUI
                          name="client_divisions"
                          label="Division(s)"
                          withId
                          options={divisionOptions}
                          wrapperClassName={styles.inputWrapper}
                          errors={errors}
                          value={(getValues('client_divisions') || []).map((division) => division.id)}
                          onChange={(ids: string[]) => {
                            const selectedOptions = divisionOptions.filter((option) => ids.includes(option.id));
                            setValue('client_divisions', selectedOptions, { shouldValidate: true });
                          }}
                        />
                      </div>
                    </div>
                  )}
                  <div className={styles.providerActionBtnContainer}>
                    <Button type="submit" className={styles.providerSubmit} disabled={isSavingProfile}>
                      Save changes
                    </Button>
                    <Button type="button" className={styles.providerCancel} onClick={onCancelEditProfile}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  /* Parent AddUserPage already provides Container + #patientDashboardPosition; avoid nested scroll layers. */
  return <>{formContent}</>;
};
export default AddCollectorUserPage;
